import { toast } from '@/components/toast';
import { useRefetchPresentersList } from '@/hooks';
import { updateClipPresenterName, updateImage, updateSceneAvatar } from '@/services/api';
import { ImageType, Presenter, VideoType, isExpressPresenter } from '@d-id/types';
import { AxiosError } from 'axios';
import { useMutation } from 'react-query';

export const useUpdateImage = (onSucess?: () => void, onError?: () => void) => {
    const { refetchPresentersList } = useRefetchPresentersList();

    const refetch = async () => {
        await refetchPresentersList();
        onSucess?.();
    };

    return useMutation(updateImage, {
        onSuccess: async () => await refetch(),
        onError: (error: AxiosError<{ kind: string; description?: string }>) => {
            const title = `Image Upload failed`;
            const message = `${error.response?.data.description}`;

            toast.info({ title, message, toastId: `upload-image-error` });
            onError?.();
        },
    });
};

interface UpdateAvatarNamePayload {
    avatar: Presenter;
    name: string;
}

export const useUpdateAvatarName = () => {
    const { refetchPresentersList } = useRefetchPresentersList();

    const errorHandler = (error: AxiosError<{ kind: string; description?: string }>) => {
        const message = `Failed to upload avatar: ${error.response?.data.description}`;

        toast.error({ title: message, toastId: `upload-avatar-error` });
    };

    const getUpdateImageData = (avatar: Presenter, name: string) => ({
        id: avatar.id,
        show: true,
        createdAt: avatar.created_at,
        presenterType: avatar.presenterType || ImageType.Upload,
        name,
    });

    return useMutation(
        ({ avatar, name }: UpdateAvatarNamePayload) => {
            if (avatar.type === VideoType.CLIP) {
                return updateClipPresenterName(avatar.id, name);
            } else if (isExpressPresenter(avatar)) {
                return updateSceneAvatar(avatar.id, { name });
            }
            return updateImage(getUpdateImageData(avatar, name));
        },
        {
            onSuccess: async () => await refetchPresentersList(),
            onError: errorHandler,
        }
    );
};
